// spinner — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

// SVG geometry constants
const SIZES = {
  sm: { diameter: 16, strokeWidth: 2 },
  md: { diameter: 24, strokeWidth: 2 },
  lg: { diameter: 32, strokeWidth: 3 },
};

export const styles = `
  :host {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .wrapper {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25em;
  }

  svg {
    display: block;
    overflow: visible;
  }

  .track {
    stroke: var(--run-spinner-track-color, var(--run-color-border-subtle));
    fill: none;
    stroke-width: var(--run-spinner-stroke-width, 1.5px);
  }

  .indicator {
    fill: none;
    stroke-width: var(--run-spinner-stroke-width, 1.5px);
    stroke: var(--run-spinner-indicator-color, var(--run-color-icon-primary));
    stroke-linecap: round;
    transform-origin: center;
    animation: run-spinner-rotate var(--run-spinner-duration, var(--run-motion-semantic-attention-duration, 1500ms)) linear infinite;
  }

  :host([tone="primary"]) .indicator {
    stroke: var(--run-color-intent-primary-base);
  }

  :host([tone="inverse"]) .indicator {
    stroke: var(--run-color-text-inverse);
  }

  @keyframes run-spinner-rotate {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .visible-label {
    font-size: 0.75em;
    color: inherit;
  }
`;

export function render(host) {
  const { size, tone, label, hiddenLabel } = host; // declared in MVA SkinModule.render contract

  const resolvedSize = SIZES[size] ?? SIZES.md;
  const { diameter, strokeWidth } = resolvedSize;
  const r = (diameter - strokeWidth) / 2;
  const cx = diameter / 2;
  const cy = diameter / 2;
  const circumference = 2 * Math.PI * r;
  // ~25% of circumference for the indicator arc
  const arcLength = circumference * 0.25;
  const dashArray = `${arcLength} ${circumference - arcLength}`;
  // start arc at top (rotate -90deg handled via transform-origin + animation)
  const dashOffset = 0;

  const labelClass = hiddenLabel ? 'sr-only' : 'visible-label';
  const labelSpan = document.createElement('span');
  labelSpan.className = labelClass;
  // label text set via textContent (never innerHTML) — done after innerHTML assignment below

  host.shadowRoot.innerHTML = `
    <div class="wrapper" part="wrapper" role="status">
      <svg
        aria-hidden="true"
        width="${diameter}"
        height="${diameter}"
        viewBox="0 0 ${diameter} ${diameter}"
        focusable="false"
      >
        <circle
          class="track"
          part="track"
          cx="${cx}"
          cy="${cy}"
          r="${r}"
        />
        <circle
          class="indicator"
          part="indicator"
          cx="${cx}"
          cy="${cy}"
          r="${r}"
          stroke-dasharray="${dashArray}"
          stroke-dashoffset="${dashOffset}"
          transform="rotate(-90 ${cx} ${cy})"
        />
      </svg>
      <span class="${labelClass}" part="label"></span>
    </div>
  `;

  // Set label text via textContent only — never innerHTML
  host.shadowRoot.querySelector('[part="label"]').textContent = label;
}